import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const NewTable = ({
  columns, // Array of { header/label, className, render }
  data, // Array of items
  emptyMessage = "No records found",
  
  // Pagination
  currentPage = 1,
  pageSize = 5,
  totalItems = 0,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [5, 10, 20, 50]
}) => {
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const currentItems = data;

  return (
    <div className="w-full bg-white border border-slate-200 overflow-hidden shadow-xs">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-[#C91D25] divide-x divide-white/20">
              {columns.map((col, idx) => (
                <th key={idx} className={`py-3 px-4 text-xs font-semibold tracking-wide text-white ${col.className || ''}`}>
                  {col.label || col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-xs">
            {currentItems.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="py-12 text-center text-slate-400 font-semibold text-sm">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              currentItems.map((item, index) => (
                <tr key={item._id || item.id || index} className={`transition-colors border-b border-slate-100 ${index % 2 === 0 ? 'bg-slate-50/40' : 'bg-white'} hover:bg-slate-100/80`}>
                  {columns.map((col, cIdx) => (
                    <td key={cIdx} className={`py-3.5 px-4 ${col.className || ''}`}>
                      {col.render ? col.render(item, index) : item[col.key || col.dataIndex]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {(onPageChange || onPageSizeChange) && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-slate-200 bg-white">
          <div className="text-[12px] text-slate-500 font-medium">
            Showing <strong className="text-slate-800">{totalItems > 0 ? (currentPage - 1) * pageSize + 1 : 0}</strong> to <strong className="text-slate-800">{Math.min(currentPage * pageSize, totalItems)}</strong> of <strong className="text-slate-800">{totalItems}</strong> items
          </div>

          <div className="flex items-center gap-4">
            {/* Rows Per Page Selector */}
            {onPageSizeChange && (
              <div className="flex items-center gap-2 text-[12px] text-slate-500 font-medium">
                <span>Rows per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                  className="h-8 px-2 text-[12px] bg-white border border-slate-200 outline-none text-slate-700 font-semibold cursor-pointer shadow-xs"
                >
                  {pageSizeOptions.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Page Buttons */}
            {onPageChange && (
              <div className="flex items-center gap-1">
                <button
                  disabled={currentPage === 1}
                  onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
                  className="p-1.5 border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed shadow-xs transition-all cursor-pointer"
                >
                  <ChevronLeft size={16} />
                </button>
                
                <span className="px-3 py-1 text-[12px] font-bold text-slate-800 bg-slate-50 border border-slate-200">
                  {currentPage} / {totalPages}
                </span>

                <button
                  disabled={currentPage === totalPages}
                  onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
                  className="p-1.5 border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed shadow-xs transition-all cursor-pointer"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
